import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { HugeiconsIcon } from '@hugeicons/react'
import { DragDropVerticalIcon } from '@hugeicons/core-solid-rounded'
import { AdminAPI, getFragment, SharedAPI } from '@guestgoodie/graphql'
import { useQuery } from '@apollo/client/react'

import {
  draggable,
  dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import { GoodieTile } from '@/features/goodies'
import Checkbox from '@/components/ui/Checkbox'
import { DropPositions } from '../types'

interface Props {
  selected: boolean
  id: number
  position: number
  readOnly: boolean
  onSelected: () => void
  onChangePosition: (position: number) => void
}

export default function GoodieRow({
  selected,
  position,
  id,
  readOnly,
  onChangePosition,
  onSelected
}: Props) {
  const elementRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)

  const [dragging, setDragging] = useState(false)
  const [draggedOver, setDraggedOver] = useState(false)
  const [overPosition, setOverPosition] = useState<DropPositions | null>(null)

  const { data } = useQuery(AdminAPI.goodie.GET, {
    variables: { id }
  })

  const title = useMemo(() => {
    return getFragment(SharedAPI.goodie.GOODIE_FRAGMENT, data?.getGoodie)?.title
  }, [data?.getGoodie])

  useEffect(() => {
    if (!elementRef.current) return
    if (!handleRef.current) return

    return draggable({
      element: elementRef.current,
      dragHandle: handleRef.current,
      getInitialDataForExternal: () => ({ 'text/plain': title }),
      onDragStart: () => setDragging(true),
      onDrop({ location }) {
        setDragging(false)

        const destination = location.current.dropTargets[0]

        if (!destination) return

        const destPosition = destination.data.position as number
        const overPosition = destination.data.overPosition as DropPositions

        if (overPosition === DropPositions.top) {
          if (destPosition > position) onChangePosition(destPosition - 1)
          else onChangePosition(destPosition)
        }

        if (overPosition === DropPositions.bottom) {
          if (destPosition > position) onChangePosition(destPosition)
          else onChangePosition(destPosition + 1)
        }
      }
    })
  }, [position, title, setDragging, onChangePosition])

  useEffect(() => {
    if (!elementRef.current) return

    return dropTargetForElements({
      element: elementRef.current,
      onDragEnter: () => setDraggedOver(true),
      onDragLeave: () => setDraggedOver(false),
      onDrag({ location }) {
        const mouseY = location.current.input.clientY
        const elementRect = elementRef.current?.getBoundingClientRect()

        if (!elementRect) return

        const elementMiddle = elementRect.top + elementRect.height / 2

        setOverPosition(
          mouseY < elementMiddle ? DropPositions.top : DropPositions.bottom
        )
      },
      onDrop: () => setDraggedOver(false),
      getData: () => ({ position, overPosition })
    })
  }, [position, overPosition, setDraggedOver])

  return (
    <div className="flex flex-col" ref={elementRef}>
      <div
        className={clsx(
          '-mt-px h-[2px] w-full rounded bg-primary opacity-0',
          draggedOver && overPosition === DropPositions.top && 'opacity-100'
        )}
      />
      <div
        className={clsx(
          'flex flex-row items-center gap-2 py-2',
          dragging && 'opacity-50'
        )}
      >
        {!readOnly && (
          <>
            <Checkbox onChange={onSelected} checked={selected} />
            <div
              className="size-6 cursor-grab rounded-[5px] text-text hover:bg-primary-bright"
              ref={handleRef}
            >
              <HugeiconsIcon
                icon={DragDropVerticalIcon}
                className="size-full"
              />
            </div>
          </>
        )}
        <GoodieTile id={id} />
      </div>
      <div
        className={clsx(
          '-mb-px h-[2px] w-full rounded bg-primary opacity-0',
          draggedOver && overPosition === DropPositions.bottom && 'opacity-100'
        )}
      />
    </div>
  )
}
